/**
 * Which project the Engineering Floor is looking at: every project, or one.
 *
 * Beside the team filter, not instead of it: a manager can look at one team's people on one
 * project. A session's project is the one its repository belongs to — known from the agent's git
 * remote — else its specification's, else its task's. With every project shown, each agent carries
 * its project over its head; with one chosen, only the agents working on it are in the room.
 *
 * Open to anybody who can see the floor, unlike the team filter: the project list is everybody's
 * to read. Remembered per browser; a remembered project that no longer exists reads as "all".
 */

import { useCallback, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from './api'

const KEY = 'devpulse:project-filter'

export interface ProjectOption {
  id: string
  name: string
  key: string
}

export interface ProjectFilter {
  projects: ProjectOption[]
  /** The project being looked at, or undefined for all of them. */
  projectId: string | undefined
  project: ProjectOption | undefined
  setProjectId: (id: string | undefined) => void
  /** Whether a picker is worth drawing: there is a project to choose. */
  show: boolean
  /** False until the list is known when a project was remembered, so the floor does not load everybody first. */
  ready: boolean
}

function remembered(): string | undefined {
  try {
    return localStorage.getItem(KEY) ?? undefined
  } catch {
    return undefined
  }
}

export function useProjectFilter(): ProjectFilter {
  const query = useQuery({ queryKey: ['repository-projects'], queryFn: api.repositories.projects, staleTime: 60_000 })
  const [stored, setStored] = useState<string | undefined>(remembered)
  const projects = query.data ?? []
  const project = stored ? projects.find((p) => p.id === stored) : undefined

  const setProjectId = useCallback((id: string | undefined) => {
    setStored(id)
    try {
      if (id) localStorage.setItem(KEY, id)
      else localStorage.removeItem(KEY)
    } catch {
      // Storage blocked: the choice still holds for this visit.
    }
  }, [])

  return {
    projects,
    projectId: project?.id,
    project,
    setProjectId,
    show: projects.length > 0,
    ready: !stored || query.isFetched,
  }
}

/** What a session's project is called over a figure: its name, else its key, else its repository's name. */
export function projectLabel(session: { projectName?: string | null; projectKey?: string | null; repositoryName?: string | null } | undefined): string | null {
  if (!session) return null
  return session.projectName?.trim() || session.projectKey?.trim() || session.repositoryName?.trim() || null
}
